/* eslint-disable no-console -- We want to warn in the console if smt happened */
'use client'

import type {
  CartItem,
  CategoryDTO,
  CheckoutProduct,
  Currency,
  Option,
  ProductLightDTO
} from 'ecosystem'
import { sendGTMEvent } from '@next/third-parties/google'
import { useMemo } from 'react'
import { type GaPurchaseOrder } from './types'
import { ecommerceItemFactory } from './ecommerce-item-factory'

interface Config {
  categories: Option<CategoryDTO[]>
}

const useGoogleAnalytics = function ({ categories }: Config) {
  const categoryMap = useMemo(() => {
    if (!categories) return {}

    const map: Record<string, string> = {}
    const processCategoryTree = (category: CategoryDTO) => {
      map[category.id] = category.name

      if (category.subCategories) {
        category.subCategories.forEach(processCategoryTree)
      }
    }

    categories.forEach(processCategoryTree)
    return map
  }, [categories])

  const trackEvent = (eventName: string, eventData: Record<string, any>) => {
    try {
      sendGTMEvent({ event: eventName, ...eventData })
    } catch (e) {
      console.warn(`Analytics - could not track event: ${eventName}`, e)
    }
  }

  // ******************* GOOGLE ANALYTICS API - COMMON *****************

  const pageView = (url: string) => {
    try {
      trackEvent('page_view', { page_path: url })
    } catch (e) {
      console.warn('Analytics - could not update page path')
    }
  }

  // ******************* STENLUNDS PROFESSIONAL ONLY ***********************

  const selectContent = (content: { content_type: string; item_id: string }) => {
    try {
      trackEvent('select_content', content)
    } catch (e) {
      console.warn('Analytics - could not update select content')
    }
  }

  const contactFormSubmit = (pagePath: string) => {
    if (typeof window !== 'undefined') {
      try {
        trackEvent('contact_form_submit', { page_path: pagePath })
      } catch (e) {
        console.warn('Analytics - could not update contact form submit')
      }
    }
  }

  // ******************* GA4 ECOMMERCE ITEM FACTORY ***********************
  /* Matches GA4 Ecommerce Data Structure (more fields can be added)
   https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm
   */
  const itemFactory = (items: CartItem[], currencyCode: Currency) => {
    return ecommerceItemFactory(items, currencyCode, categoryMap)
  }

  // ******************* GOOGLE ANALYTICS API - ECOMMERCE *****************

  /**
   * Track view_item_list event
   * @param items - List of items being viewed
   * @param listId - Optional ID of the list
   * @param listName - Optional name of the list
   * @param currency - Currency code
   */
  const viewItemList = (
    items: ProductLightDTO[],
    listId: string | undefined,
    listName: string | undefined,
    currency: Currency
  ) => {
    try {
      const ecommerceItems = itemFactory(items, currency)

      const enrichedItems = ecommerceItems.map((item) => ({
        ...item,
        ...(listId && { item_list_id: listId }),
        ...(listName && { item_list_name: listName })
      }))

      trackEvent('view_item_list', {
        ecommerce: {
          currency,
          items: enrichedItems
        }
      })
    } catch (e) {
      console.warn('Analytics - could not track view_item_list')
    }
  }

  /**
   * Track view_item event (product detail view)
   * @param item - Item being viewed
   * @param currency - Currency code
   */
  const viewItem = (item: ProductLightDTO, currency: Currency) => {
    try {
      trackEvent('view_item', {
        ecommerce: {
          currency,
          value: item.price?.toFixed(2) || '0.00',
          items: itemFactory([item], currency)
        }
      })
    } catch (e) {
      console.warn('Analytics - could not track view_item')
    }
  }

  /**
   * Track add_to_cart or remove_from_cart events
   * @param action - 'add' or 'remove'
   * @param value - Total value of the items
   * @param currency - Currency code
   * @param items - List of items added/removed
   */
  const updateCart = (
    action: 'add' | 'remove',
    value: number,
    currency: Currency,
    items: CartItem[]
  ) => {
    try {
      const eventName = action === 'add' ? 'add_to_cart' : 'remove_from_cart'

      trackEvent(eventName, {
        ecommerce: {
          currency,
          value: Number(value).toFixed(2),
          items: itemFactory(items, currency)
        }
      })
    } catch (e) {
      console.warn(
        `Analytics - could not track ${action === 'add' ? 'add_to_cart' : 'remove_from_cart'}`
      )
    }
  }

  /**
   * Track view_cart event
   * @param items - Items in cart
   * @param value - Total cart value
   * @param currency - Currency code
   */
  const viewCart = (items: CartItem[], value: number, currency: Currency) => {
    try {
      trackEvent('view_cart', {
        ecommerce: {
          currency,
          value: Number(value).toFixed(2),
          items: itemFactory(items, currency)
        }
      })
    } catch (e) {
      console.warn('Analytics - could not track view_cart')
    }
  }

  /**
   * Track begin_checkout event
   * @param products - Items in checkout
   * @param value - Total checkout value
   * @param currency - Currency code
   */
  const checkout = (products: CartItem[], value: number, currency: Currency) => {
    try {
      trackEvent('begin_checkout', {
        ecommerce: {
          currency,
          value: Number(value).toFixed(2),
          items: itemFactory(products, currency)
        }
      })
    } catch (e) {
      console.warn('Analytics - could not track begin_checkout')
    }
  }

  const minorUnitsToFloatParser = (value: number | undefined) => {
    if (value === 0 || value === undefined || value === null || isNaN(value)) return '0.00'
    return Number(value / 100).toFixed(2)
  }

  /**
   * Track purchase event
   * @param order - Order information
   * @param orderProducts - Products in the order
   * @param currency - Currency of the order
   */
  const purchase = (
    order: GaPurchaseOrder,
    orderProducts: CheckoutProduct[],
    currency: Currency
  ) => {
    try {
      const cartItemCompatible = orderProducts.map((p) => ({
        id: p.productId,
        name: p.name,
        brand: { name: p.brand },
        price: p.unitPrice,
        qty: p.quantity.toString(),
        slug: p.slug
      }))

      const items = itemFactory(cartItemCompatible as unknown as CartItem[], currency)

      const payload = {
        ecommerce: {
          transaction_id: order.id,
          affiliation: 'Online Store',
          value: minorUnitsToFloatParser(order.totalAmount),
          tax: minorUnitsToFloatParser(order.taxAmount),
          shipping: minorUnitsToFloatParser(order.shippingPrice),
          currency: order.currency,
          items
        }
      }

      trackEvent('purchase', payload)
    } catch (e) {
      console.warn('Analytics - could not track purchase')
    }
  }

  // ******************* GOOGLE ANALYTICS API - SEARCH *****************

  const search = (searchTerm: string) => {
    try {
      trackEvent('search', { search_term: searchTerm })
    } catch (e) {
      console.warn('Analytics - could not track search')
    }
  }

  const viewPdp = (pagePath: string) => {
    try {
      trackEvent('view_pdp', { page_path: pagePath })
    } catch (e) {
      console.warn('Analytics - could not track view_pdp')
    }
  }

  return {
    // Common events
    pageView,
    contactFormSubmit,
    selectContent,

    // E-commerce events
    viewItemList,
    viewItem,
    updateCart,
    viewCart,
    checkout,
    purchase,

    // Search events
    search,
    viewPdp
  }
}

export default useGoogleAnalytics
